import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	type CalendarEvent,
	getCalendarEvents,
} from "@/shared/repositories/CalendarEventRepository";
import type {
	CalendarDayShift,
	CalendarWorkOrder,
} from "@/shared/repositories/WorkOrderRepository";
import type { CalendarItem } from "../components/EventBar";
import {
	addDays,
	buildShiftBands,
	type DayKind,
	dayKindOf,
	getMonthWeeks,
	toDateKey,
} from "../utils/calendarUtils";

const moment = getMomentTz();
export const useCalendarData = (
	assetId: number | null,
	currentDate: Date,
	serverTz: string,
	onError: (message: string) => void,
) => {
	// The whole visible grid, not just the month: the grey days of the
	// neighbouring months show their plan and take drops like any other day.
	// The SP cuts the range at a naive midnight compared against UTC datetimes,
	// so one padding day keeps the last evening in; the grid clips the rest.
	const weeks = getMonthWeeks(currentDate);
	const rangeStart = toDateKey(weeks[0][0]);
	const rangeEnd = toDateKey(addDays(weeks[weeks.length - 1][6], 1));

	const {
		data: calData,
		isLoading: isCalendarLoading,
		isFetching: isCalendarFetching,
		isPreviousData: isCalendarStale,
	} = useQuery({
		queryKey: ["calendarEvents", assetId, rangeStart, rangeEnd],
		queryFn: ({ signal }) =>
			getCalendarEvents(
				{ start: rangeStart, end: rangeEnd, assetId: assetId as number },
				signal,
			),
		enabled: !!assetId,
		staleTime: 30_000,
		keepPreviousData: true,
		meta: { realtime: ["FAB.GetCalendarEvents"] },
		onError: (e: Error) => onError(e.message),
	});

	const toLocalDayKey = (iso: string) =>
		moment.utc(iso).tz(serverTz).format("YYYY-MM-DD");

	const toWorkOrderAdapter = (
		ev: CalendarEvent,
		dayKey: string,
	): CalendarWorkOrder => ({
		WorkOrderId: ev.WorkOrderId as number,
		WorkOrderNumber: ev.WorkOrderNumber,
		AssetId: ev.AssetId ?? (assetId as number),
		Status: ev.Status ?? "Pending",
		Quantity: ev.Quantity ?? 0,
		Sequence: ev.Sequence ?? 0,
		Notes: ev.Notes,
		PartNumber: ev.PartNumber,
		ExtendedPartNumber: ev.ExtendedPartNumber,
		DisplayShiftId: ev.OperativeShiftId ?? ev.PlanShiftId ?? 0,
		DisplayDate: dayKey,
		DisplayShift: ev.OperativeShift ?? ev.PlanShift ?? "",
		DisplayProductionShift: 0,
		PlanShiftId: ev.PlanShiftId,
		PlanDate: ev.PlanDate,
		PlanShift: ev.PlanShift,
		IsCarriedOver: ev.IsCarriedOver,
		StartedAt: ev.StartedAt,
		CompletedAt: ev.CompletedAt,
	});

	const { workOrdersList, calendarItems } = useMemo(() => {
		const woList: CalendarWorkOrder[] = [];
		const items: CalendarItem[] = [];

		for (const ev of calData?.events ?? []) {
			const base = {
				key: ev.EventId != null ? `ev-${ev.EventId}` : `wo-${ev.WorkOrderId}`,
				start: moment.utc(ev.Start).toDate(),
				end: moment.utc(ev.End).toDate(),
				sortHint: ev.Sequence ?? 0,
			};
			if (ev.EventTypeCode === "W" && ev.WorkOrderId != null) {
				const adapter = toWorkOrderAdapter(ev, toLocalDayKey(ev.Start));
				woList.push(adapter);
				items.push({ ...base, kind: "wo", event: ev, wo: adapter });
			} else if (ev.EventTypeCode !== "W") {
				items.push({
					...base,
					kind: "other",
					event: ev,
					compact: ev.EventTypeCode === "B",
				});
			}
		}
		return { workOrdersList: woList, calendarItems: items };
	}, [calData, assetId, serverTz]);

	const shiftsByDay = useMemo(() => {
		const map = new Map<string, CalendarDayShift[]>();
		for (const ds of calData?.dayShifts ?? []) {
			const key = ds.Date.slice(0, 10);
			const list = map.get(key) ?? [];
			list.push(ds);
			map.set(key, list);
		}
		return map;
	}, [calData]);

	const defaultShiftCount = calData?.dayShifts[0]?.DefaultShiftsPerDay;
	const lineDefaultKind: DayKind | null =
		defaultShiftCount === 3 ? "8h" : defaultShiftCount === 2 ? "12h" : null;

	const scheduleByDay = useMemo(() => {
		const map = new Map<string, DayKind>();
		for (const [key, rows] of shiftsByDay) {
			const kind = dayKindOf(rows);
			if (kind && rows[0]?.IsOverride) map.set(key, kind);
		}
		return map;
	}, [shiftsByDay]);
	const shiftBandsByDay = useMemo(
		() => buildShiftBands(calData?.dayShifts, serverTz),
		[calData, serverTz],
	);
	return {
		calData,
		isCalendarLoading,
		isCalendarFetching,
		isCalendarStale,
		workOrdersList,
		calendarItems,
		shiftsByDay,
		lineDefaultKind,
		scheduleByDay,
		shiftBandsByDay,
	};
};
